import * as ngCore from '@angular/core'

/**
 * Owns destroyable state instances created inside one route injector.
 *
 * Some state objects register NgRx feature/effect trees after construction and
 * need an explicit `destroy(...)` call when their route scope goes away. This
 * registry keeps that cleanup tied to the route injector without
 * exposing a general-purpose state registry to the rest of the app.
 *
 * The route boundary component calls `destroyAll(...)` when the app route
 * exits. The registration API is intentionally private to this file; callers
 * should use {@link Registry.provideDestroyable} so route-bound state is
 * registered at creation time.
 */
@ngCore.Injectable()
export class Registry {
  /**
   * Creates the backing state for this route-bound registry instance.
   */
  public constructor() {
    Registry.State.initialize(this)

    ngCore.inject(ngCore.DestroyRef).onDestroy(() => {
      this.destroyAll('RouteBound.InjectorDestroyed')
    })
  }

  /**
   * Destroys every state instance created in this route-bound registry.
   *
   * Cleanup runs in reverse creation order so later-created dependents are torn
   * down before the state they may have consumed. The method is idempotent
   * because Angular and local lifecycle hooks can both reach cleanup paths.
   */
  public destroyAll(source: string): void {
    Registry.State.destroyAll(this, source)
  }
}
export namespace Registry {
  /**
   * Minimal lifecycle contract required for state owned by this registry.
   */
  export interface Destroyable {
    destroy(source: string): void
  }

  /**
   * Provides a route-bound state type and registers the created instance for
   * explicit route-scope cleanup.
   *
   * The factory is lazy: Angular only creates the instance when a descendant of
   * the route injects it. This lets broad provider groups exist on
   * the route without eagerly loading every collection.
   */
  export function provideDestroyable<T extends Registry.Destroyable>(
    type: ngCore.Type<T>,
  ): ngCore.Provider {
    return {
      provide: type,
      useFactory: () => {
        const registry = ngCore.inject(Registry)

        // These state classes use Angular's `inject(...)` internally, so
        // creating them inside the provider factory binds their dependencies to
        // the current route injector.
        const instance = new type()

        return Registry.State.register(registry, instance)
      },
    }
  }

  /**
   * Private backing store for {@link Registry} instances.
   *
   * The namespace lives under `Registry` so all implementation details are
   * grouped with the class, while the actual state remains hidden in a WeakMap.
   */
  export namespace State {
    /**
     * Mutable state for one route-bound registry instance.
     */
    export interface Value {
      readonly instances: Destroyable[]
      destroyed: boolean
    }

    // WeakMap keeps the backing state private without putting mutable fields on
    // the injectable class itself.
    const _values = new WeakMap<Registry, Value>()

    /**
     * Initializes backing state for a newly constructed registry.
     */
    export function initialize(registry: Registry): void {
      _values.set(registry, {
        destroyed: false,
        instances: [],
      })
    }

    /**
     * Tracks a newly created destroyable instance in this route scope.
     */
    export function register<T extends Destroyable>(
      registry: Registry,
      instance: T,
    ): T {
      const state = get(registry)

      // If the route scope was already torn down, immediately clean up the late
      // instance instead of leaving an orphaned NgRx feature/effect tree alive.
      if (state.destroyed) {
        instance.destroy('RouteBound.AlreadyDestroyed')
        return instance
      }

      state.instances.push(instance)
      return instance
    }

    /**
     * Destroys all tracked instances for this route scope.
     */
    export function destroyAll(registry: Registry, source: string): void {
      const state = get(registry)

      if (state.destroyed) {
        return
      }
      state.destroyed = true

      // Later-created state can depend on earlier-created state, so cleanup
      // runs in reverse creation order.
      for (let idx = state.instances.length - 1; idx >= 0; idx--) {
        state.instances[idx].destroy(source)
      }
      state.instances.length = 0
    }

    /**
     * Reads backing state for a registry that should already be initialized.
     */
    function get(registry: Registry): Value {
      const state = _values.get(registry)

      if (state === undefined) {
        throw new Error('Route-bound registry was used before construction')
      }

      return state
    }
  }
}
